import type { CityConfig } from "../city-template/types";

export const burlingtonConfig: CityConfig = {
  city: "Burlington",
  state: "Vermont",
  stateAbbr: "VT",
  slug: "burlington",
  route: "/cities/burlington",
  title: "Emergency Passport Burlington, VT — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Burlington, VT — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Chittenden County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Burlington, VT",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Burlington and Chittenden County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Beautiful%20golden%20hour%20view%20of%20Burlington%20Vermont%20downtown%20with%20Lake%20Champlain%20waterfront%20and%20Adirondack%20Mountains%20in%20background%2C%20Church%20Street%20Marketplace%20pedestrian%20mall%20with%20historic%20brick%20buildings%20and%20local%20shops%2C%20vibrant%20New%20England%20college%20town%20atmosphere%2C%20warm%20autumn%20light%20with%20colorful%20foliage%2C%20clean%20and%20inviting%20urban%20scene%2C%20professional%20architectural%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20warm%20earth%20tones%20and%20soft%20sky%2C%20photorealistic%20cityscape&width=1800&height=700&seq=burlington-vt-hero-001&orientation=landscape",
  heroImageAlt: "Burlington Vermont downtown with Lake Champlain waterfront and Adirondack Mountains",
  geoPosition: "44.4759;-73.2121",
  geoPlacename: "Burlington, Vermont",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Vermont Passport Agency",
    address: "50 South Main Street, St. Albans, VT 05478",
    distance: "Approximately 220 miles, 3.5 hours via I-89",
  },
  nearbyCitiesZips: [
    { name: "Burlington", zips: "05401–05408" },
    { name: "South Burlington", zips: "05403, 05407" },
    { name: "Winooski", zips: "05404" },
    { name: "Essex Junction", zips: "05452, 05453" },
    { name: "Williston", zips: "05495" },
    { name: "Colchester", zips: "05439, 05446" },
    { name: "Shelburne", zips: "05482" },
    { name: "Charlotte", zips: "05445" },
    { name: "Hinesburg", zips: "05461" },
    { name: "Jericho", zips: "05465" },
    { name: "Richmond", zips: "05477" },
    { name: "Milton", zips: "05468" },
    { name: "St. Albans", zips: "05478" },
    { name: "Middlebury", zips: "05753" },
    { name: "Vergennes", zips: "05491" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Chittenden County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Montpelier", route: "/cities/montpelier" },
    { name: "Rutland", route: "/rutland" },
    { name: "Stowe", route: "/stowe" },
    { name: "Brattleboro", route: "/brattleboro" },
    { name: "Bennington", route: "/bennington" },
    { name: "Middlebury", route: "/middlebury" },
    { name: "White River Junction", route: "/white-river-junction" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Burlington remotely via secure courier. We do not have a walk-in office in Burlington — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Chittenden County and Vermont travelers.",
  fpcOfficeAddress: "50 South Main Street, St. Albans, VT 05478",
  fpcOfficeDistance: "Serving all of Chittenden County and Vermont",
  serviceAreaIntro: "From downtown Burlington's Church Street Marketplace to the UVM campus, from South Burlington to the Lake Champlain islands — Fast Passport Center serves the entire Burlington community. We regularly help travelers from Winooski, Essex Junction, Williston, and Shelburne get their passports fast.",
  nearbyCitiesIntro: "We serve all Burlington neighborhoods and nearby Chittenden County communities including South Burlington, Winooski, Essex Junction, Williston, Colchester, Shelburne, Charlotte, Hinesburg, Jericho, Richmond, Milton, St. Albans, Middlebury, and Vergennes. If you are in the greater Burlington area, we have you covered.",
  howItWorksIntro: "Most Burlington travelers complete everything online and ship documents overnight. No need to drive to Boston or wait in line at the regional agency.",
  mistakesIntro: "With Burlington International Airport (BTV) and the Canadian border just 45 miles north, Chittenden County travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Burlington and Chittenden County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Burlington? Here are the most common ones we hear from Vermont travelers.",
  processingIntro: "Burlington travelers — UVM students studying abroad, Dealer.com and Beta Technologies professionals on international business, Lake Champlain ferry travelers heading to Montreal, and Chittenden County families planning overseas vacations — have several options for getting a passport fast. Here is how they compare, and why most Vermont residents choose a U.S. Department of State registered passport courier for predictable, professional processing without the 3.5-hour drive to Boston.",
  localAreaIntro: [
    "Burlington, Vermont is the cultural and economic heart of the Green Mountain State. Home to the University of Vermont (UVM) and Champlain College, Burlington hums with the energy of over 15,000 college students — many of whom participate in study abroad programs spanning every continent. UVM's Office of International Education sends hundreds of students annually to programs in 50+ countries, and Champlain College's international internship program in Montreal and Dublin creates steady passport demand. When a UVM student receives their study abroad acceptance letter in March for a June departure — a tight 10-week window — standard passport processing at 8–11 weeks simply will not work.",
    "The tech and innovation sector adds another layer of urgency. Dealer.com, one of Burlington's largest employers, operates globally in the automotive digital marketing space. Beta Technologies, the electric aviation startup based at Burlington International Airport, regularly sends engineers and executives to international aerospace conferences and partner meetings. When a Beta engineer needs to be in Toulouse, France, for an EASA certification meeting with 5 days notice, the Vermont Passport Agency — 220 miles away down I-89 — is not a practical option. Fast Passport Center's 24–48 hour courier service is the answer.",
    "Burlington International Airport (BTV) is Vermont's primary air gateway, offering direct flights to major hubs including New York, Chicago, Washington D.C., Philadelphia, and seasonal service to Toronto. BTV's convenience — just 3 miles from downtown — means Burlington residents book flights with the same ease as major-city dwellers. But BTV is a spoke airport: all international travel connects through hubs, and airlines verify passport validity at the BTV check-in counter for the entire international itinerary. A Burlington traveler with an expired passport will be denied boarding at BTV before they even reach their connecting flight.",
    "Beyond students and professionals, Burlington's proximity to Canada creates unique cross-border travel patterns. Montreal is just a 2-hour drive north on I-89, making weekend trips to Quebec second nature for Chittenden County families. Many Burlington residents have enhanced driver's licenses for land crossings — but enhanced licenses are not valid for air travel. When a family decides at the last minute to fly to Toronto instead of drive, or when a Burlington resident extends a Montreal shopping trip into a flight to Europe from Montréal-Trudeau Airport (YUL), they discover their enhanced license is not enough. A valid U.S. passport becomes essential."
  ],
  statePageRoute: "/states/vermont",
  statePageName: "Vermont",
  dmvWebsite: "https://dmv.vermont.gov",
  dmvLabel: "Vermont DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Burlington international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Rachel M.",
      location: "Burlington, VT",
      rating: 5,
      text: "My UVM semester abroad in Ireland was starting in 10 days and my passport was expired. Fast Passport Center got me a new one in 36 hours. Absolutely saved my study abroad experience.",
      service: "Emergency Passport",
    },
    {
      name: "James T.",
      location: "South Burlington, VT",
      rating: 5,
      text: "Needed to renew for a business trip to Montreal. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend for Vermont professionals.",
      service: "Passport Renewal",
    },
    {
      name: "Sarah K.",
      location: "Winooski, VT",
      rating: 5,
      text: "Lost my passport the week before my vacation to the Dominican Republic. They walked me through the DS-64 process and got me a replacement in 2 days. Incredible service for Chittenden County.",
      service: "Lost Passport",
    },
    {
      name: "David R.",
      location: "Williston, VT",
      rating: 5,
      text: "As a Beta Technologies engineer traveling to aerospace conferences, I need reliable passport service. Fast Passport Center has been my go-to. Professional and fast.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "burlington-vt-1",
      name: "Burlington Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "11 Elmwood Ave",
      city: "Burlington",
      state: "VT",
      zip: "05401",
      phone: "(802) 863-6033",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Burlington",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Burlington. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "burlington-vt-2",
      name: "South Burlington Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "275 White St",
      city: "South Burlington",
      state: "VT",
      zip: "05403",
      phone: "(802) 862-0339",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "2 miles from downtown Burlington",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for South Burlington residents. Limited hours — book appointments well in advance.",
      icon: "ri-mail-line",
    },
    {
      id: "burlington-vt-3",
      name: "Essex Junction Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "12 Main St",
      city: "Essex Junction",
      state: "VT",
      zip: "05452",
      phone: "(802) 878-5113",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "6 miles from downtown Burlington",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for Essex and Williston residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "burlington-vt-4",
      name: "Williston Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "14 Park Ave",
      city: "Williston",
      state: "VT",
      zip: "05495",
      phone: "(802) 878-5563",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "7 miles from downtown Burlington",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for eastern Chittenden County residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "burlington-vt-5",
      name: "Shelburne Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "5420 Shelburne Rd",
      city: "Shelburne",
      state: "VT",
      zip: "05482",
      phone: "(802) 985-5217",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "7 miles south of Burlington",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves southern Chittenden County. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "burlington-vt-6",
      name: "Burlington City Clerk",
      type: "City Clerk Passport Acceptance",
      address: "149 Church St",
      city: "Burlington",
      state: "VT",
      zip: "05401",
      phone: "(802) 865-7000",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.1 miles from City Hall",
      website: "https://www.burlingtonvt.gov/",
      websiteLabel: "Burlington City Clerk",
      notes: "Major municipal facility in downtown Burlington. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 220 miles to Boston", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
    { feature: "Weekend Availability", agency: "No — business hours only", fpc: "Yes — we work weekends for urgent cases" },
    { feature: "Photo Review", agency: "No — your problem if rejected", fpc: "Yes — pre-submission verification" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Vermont photo shops often use generic sizes. Passport photos must be exactly 2x2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Chittenden County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Burlington applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Vermont will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Burlington families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Burlington travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Burlington." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Burlington address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Burlington before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Burlington for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround for most Vermont travelers." },
    { q: "How fast can I renew my passport in Burlington?", a: "Fast Passport Center offers fast passport renewal in Burlington in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value." },
    { q: "What if my passport expires before my trip to Canada?", a: "If your passport expires before your trip to Canada, contact Fast Passport Center immediately. We can expedite your passport renewal in Burlington in 24–48 hours so you don't miss your departure. Note: enhanced driver's licenses work for land crossings but NOT for air travel to Canada." },
    { q: "Can I get a same-day passport appointment near Burlington?", a: "Same-day passport appointments are only available at the Vermont Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Burlington residents — without the 3.5-hour drive to Boston." },
    { q: "Can I expedite a child passport in Burlington?", a: "Yes. Fast Passport Center expedites child passports in Burlington for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Burlington with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "How fast can I get a passport in Burlington?", a: "With Fast Passport Center's emergency courier service, Burlington travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays." },
    { q: "Do I need to visit the Vermont Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Where are the closest passport acceptance facilities in Burlington?", a: "Popular options include the Burlington Main Post Office (05401), South Burlington Post Office (05403), Essex Junction Post Office (05452), and Burlington City Clerk (05401). All require appointments. We help you find the most convenient location and schedule your visit." },
    { q: "Can I get a same-day passport in Burlington?", a: "Same-day passports are only available at the Vermont Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option." },
    { q: "What if I live in a nearby town like Williston or Shelburne?", a: "We serve all of Chittenden County including South Burlington, Winooski, Essex Junction, Williston, Colchester, Shelburne, Charlotte, Hinesburg, Jericho, Richmond, Milton, and St. Albans. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed." },
    { q: "How much does expedited passport service cost in Burlington?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Burlington", address: "1127 North Ave, Burlington, VT 05408", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Burlington", address: "400 Shelburne Rd, Burlington, VT 05401", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — South Burlington", address: "100 Dorset St, South Burlington, VT 05403", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Williston", address: "25 Wentworth Dr, Williston, VT 05495", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Williston", address: "863 Harvest Ln, Williston, VT 05495", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Essex Junction", address: "18 Pearl St, Essex Junction, VT 05452", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Costco — Colchester", address: "218 Lower Mountain View Dr, Colchester, VT 05446", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    { name: "VT DMV — South Burlington", address: "1189 Williston Rd, South Burlington, VT 05403", hours: "Mon–Fri: 7:45AM–4:30PM", note: "REAL ID and driver's license renewals. Appointment required.", featured: true },
    { name: "VT DMV — Montpelier", address: "120 State St, Montpelier, VT 05602", hours: "Mon–Fri: 7:45AM–4:30PM", note: "Main office. Passport-ready photo services available. Online appointment booking.", featured: false },
    { name: "VT DMV — Rutland", address: "101 State Pl, Rutland, VT 05701", hours: "Mon–Fri: 7:45AM–4:30PM", note: "REAL ID processing. Walk-ins limited — book online first.", featured: false },
  ],
  airports: [
    { code: "BTV", name: "Burlington International Airport", distance: "3 miles from downtown Burlington", address: "1200 Airport Dr, South Burlington, VT 05403", note: "Vermont's largest airport. Direct flights to major U.S. hubs and seasonal international service to Toronto. Primary gateway for Chittenden County travelers.", flightUrl: "https://www.btv.aero/", flightLabel: "BTV Flight Info", featured: true },
    { code: "YUL", name: "Montréal-Trudeau International Airport", distance: "105 miles from Burlington", address: "975 Roméo-Vachon Blvd N, Dorval, QC H4Y 1H1", note: "Major international hub for flights to Europe, Asia, and beyond. Many Burlington residents drive 2 hours to YUL for direct international flights.", flightUrl: "https://www.admtl.com/", flightLabel: "Montréal Airport", featured: false },
    { code: "BOS", name: "Boston Logan International Airport", distance: "220 miles from Burlington", address: "1 Harborside Dr, Boston, MA 02128", note: "Major international hub for long-haul flights to Europe, Asia, and South America. Accessible via I-89 and I-93.", flightUrl: "https://www.massport.com/logan-airport", flightLabel: "Logan Airport", featured: false },
  ],
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from the Vermont Department of Health Vital Records Office in Burlington. Online, mail, or in-person options available for Chittenden County residents.", url: "https://www.healthvermont.gov/health-statistics-vital-records/vital-records-population-data" },
    { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through VT Department of Health Vital Records. Required for estate, insurance, and legal matters.", url: "https://www.healthvermont.gov/health-statistics-vital-records/vital-records-population-data" },
    { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Burlington City Clerk at 149 Church Street. Certified copies available for name changes and legal purposes.", url: "https://www.burlingtonvt.gov/" },
    { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through the Chittenden County Family Court. Required for name change passport corrections.", url: "https://www.vermontjudiciary.org/courts/family-division" },
  ],
};